/*
 * window.VoyadoClient — Elevate Storefront (v3) query + notification client.
 * Owns customerKey/sessionKey/consent gating ourselves rather than via the
 * Accelerator SDK. API: autocomplete/searchPage/landingPage/productPage/cartPage
 * queries + notifyAddToCart/notifyAddFavorite/ensureCartStamped seams.
 * Gotcha: queries are gated on OneTrust consent (Functional group C0003); ANONYMOUS when denied.
 */
;(function () {
  'use strict';

  const URL_PARAMS = new URL(window.location.href).searchParams;
  const PREVIEW = URL_PARAMS.get('PREVIEW') === 'true';

  const QUERY_PATH = { autocomplete: 'autocomplete', searchPage: 'search-page', landingPage: 'landing-page', productPage: 'product-page', cartPage: 'cart-page' };
  const SESSION_KEY = 'brora.voyado.session';

  // Elevate cluster host from window.broraVoyado.clusterUrl (theme Cluster ID setting, present at first paint) —
  // NOT the Accelerator's async window.elevate.clusterUrl, which raced the PLP's first query and left the grid empty (D82).
  function clusterBase() {
    const u = window.broraVoyado && window.broraVoyado.clusterUrl;
    return typeof u === 'string' && u ? u.replace(/\/+$/, '') : null;
  }
  function cfg() { return window.broraVoyado || {}; }
  // Elevate only defines DESKTOP/MOBILE and prescribes no pixel breakpoint, so align with the layout the
  // visitor actually sees: the theme's desktop breakpoint (--breakpoint-lg = 1024px), the same token the
  // other section scripts read as DESKTOP_BREAKPOINT. (Was a hardcoded 768, which reported 768–1023px —
  // still the mobile layout — as DESKTOP.)
  const DESKTOP_BREAKPOINT = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--breakpoint-lg'), 10) || 1024;
  function touchpoint() { return window.innerWidth < DESKTOP_BREAKPOINT ? 'MOBILE' : 'DESKTOP'; }


  // Consent gate — Voyado is CRM personalisation, so gate on OneTrust's Functional group C0003 (deny by default).
  // If the OneTrust admin re-maps categories, update CONSENT_GROUP (Voyado cookie not yet categorised there — #137).
  const CONSENT_GROUP = 'C0003';

  // Accepted groups as ",C0001,C0003,". Read the OptanonConsent cookie first — it's the authoritative
  // synchronous store of consent, so a returning consenter keys correctly before the OnetrustActiveGroups
  // global settles (which can seed an incomplete placeholder). Fall back to the global (refreshed on
  // OneTrustGroupsUpdated); null when neither is available.
  function oneTrustGroups() {
    try {
      const m = document.cookie.match(/(?:^|;\s*)OptanonConsent=([^;]+)/);
      if (m) {
        const raw = (decodeURIComponent(m[1]).match(/(?:^|&)groups=([^&]*)/) || [])[1] || '';
        const accepted = raw.split(',').filter(function (p) { return /:1$/.test(p); })
          .map(function (p) { return p.split(':')[0]; });
        if (accepted.length) return ',' + accepted.join(',') + ',';
      }
    } catch (e) {}
    const g = window.OnetrustActiveGroups || window.OptanonActiveGroups;
    if (typeof g === 'string' && g) return g;
    return null;
  }

  function trackingAllowed() {
    const groups = oneTrustGroups();
    return groups !== null && groups.indexOf(',' + CONSENT_GROUP + ',') !== -1; // null (unresolved) → deny
  }
  function randomKey() {
    try { return crypto.randomUUID(); }
    catch (e) { return 'anon-' + Math.random().toString(36).slice(2) + Date.now().toString(36); }
  }

  // Reads the stored identity without creating one. Storing an identifier on the device needs consent
  // in its own right, whether or not it is ever sent, so the denial path reads through this.
  function readSession() {
    try {
      const raw = localStorage.getItem(SESSION_KEY);
      const s = raw ? JSON.parse(raw) : null;
      if (s && s.customerKey && s.sessionKey) return s;
    } catch (e) { /* private mode / malformed */ }
    return null;
  }

  function clearSession() {
    try { localStorage.removeItem(SESSION_KEY); } catch (e) { /* private mode */ }
  }

  // Persistent identity — a stable random customerKey/sessionKey in our own
  // storage (not the Accelerator's voyado.session), reused across page loads.
  function persistentSession() {
    const stored = readSession();
    if (stored) return stored;
    const fresh = { customerKey: randomKey(), sessionKey: randomKey() };
    try { localStorage.setItem(SESSION_KEY, JSON.stringify(fresh)); } catch (e) { /* quota / private mode */ }
    return fresh;
  }
  // No consent → ANONYMOUS user context with both keys omitted (sending keys
  // alongside userContext is an error).
  function identity() {
    if (!trackingAllowed()) return { userContext: 'ANONYMOUS' };
    const s = persistentSession();
    return { customerKey: s.customerKey, sessionKey: s.sessionKey };
  }

  // Serialise to a query string, dropping null/empty values.
  function toQS(obj) {
    const usp = new URLSearchParams();
    Object.keys(obj).forEach(function (k) {
      const v = obj[k];
      if (v != null && v !== '') usp.set(k, v);
    });
    return usp.toString();
  }

  function queryString(params) {
    const c = cfg();
    const id = identity();
    const merged = Object.assign({
      market: c.market,
      locale: c.locale,
      touchpoint: touchpoint()
    }, params || {});
    if (id.userContext) {
      // `notify` can't be combined with any `userContext` (Elevate 400), so translate the live-typing
      // preview flag (notify=false) into SYNTHETIC — the doc-sanctioned notify=false replacement (a
      // faux query: no metrics collected, results still returned) — so an anonymous partial term is
      // never registered. A committed query keeps the real ANONYMOUS context. Then drop notify.
      merged.userContext = (merged.notify === 'false') ? 'SYNTHETIC' : id.userContext;
      delete merged.notify;
    } else { merged.customerKey = id.customerKey; merged.sessionKey = id.sessionKey; }
    if (PREVIEW) merged.viewId = 'preview';
    return toQS(merged);
  }

  function runQuery(name, params, body) {
    const base = clusterBase();
    if (!base) return Promise.reject(new Error('voyado_missing'));
    const url = base + '/api/storefront/v3/queries/' + QUERY_PATH[name] + '?' + queryString(params);
    // autocomplete is always POST; other queries are GET unless a body is supplied.
    const hasBody = name === 'autocomplete' || !!body;
    // cache: 'no-store' — the Elevate response (order, included products, tickets) must never be cached; a
    // stale/cached response breaks behavioural tracking + purchase attribution (Elevate docs, "Caching").
    const opts = { method: hasBody ? 'POST' : 'GET', cache: 'no-store', headers: { Accept: 'application/json' } };
    if (hasBody) {
      // Keeps the POST a CORS simple request. application/json is not on the safelist, so it costs an
      // OPTIONS preflight — a second round trip per query. Elevate documents this one; the body is JSON.
      opts.headers['Content-Type'] = 'text/plain;charset=utf-8';
      opts.body = JSON.stringify(body || {});
    }
    // 8s timeout — abort a hung request; surfaces as 'voyado_timeout' to the caller.
    const ac = new AbortController();
    const timer = setTimeout(function () { ac.abort(); }, 8000);
    opts.signal = ac.signal;
    return fetch(url, opts).then(function (r) {
      if (!r.ok) throw new Error('voyado_http_' + r.status);
      return r.json();
    }).catch(function (e) {
      throw (e && e.name === 'AbortError') ? new Error('voyado_timeout') : e;
    }).finally(function () { clearTimeout(timer); });
  }

  // Low-level notification POST — consent-gated, fire-and-forget (keepalive).
  // `body` is { ticket } for click/add-to-cart, { productKey } for favourites.
  function postNotification(path, body) {
    const base = clusterBase();
    if (!base || !trackingAllowed()) return;
    const s = persistentSession();
    const qs = toQS({ customerKey: s.customerKey, market: cfg().market, sessionKey: s.sessionKey });
    fetch(base + '/api/storefront/v3/notifications/' + path + '?' + qs, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true
    }).catch(function () {});
  }

  // Ticket-based notifications (click, add-to-cart) — ticket comes from a query result.
  function notify(path, ticket) {
    if (!ticket) return;
    postNotification(path, { ticket: ticket });
  }

  // Favourite notifications — keyed by productKey (= Shopify product id), no ticket.
  function notifyFavorite(path, productKey) {
    if (productKey == null || productKey === '') return;
    postNotification(path, { productKey: String(productKey) });
  }

  // Cart-attribute bridge — the checkout-sandbox purchase Web Pixel can't read our
  // localStorage, so stamp the Elevate identity onto cart attributes (names must match the pixel).
  const CART_ATTR_CUSTOMER = '_elevate_customer_key';
  const CART_ATTR_SESSION = '_elevate_session_key';
  const CART_ATTR_MARKET = '_elevate_market';
  // Locale-aware cart root (multi-market store); falls back to '/'.
  function cartRoot() {
    const r = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return typeof r === 'string' && r ? r : '/';
  }
  let cartStamping = false; // drop overlapping passes
  function ensureCartStamped() {
    // Consent gate — never stamp a non-consenting cart. On deny/withdrawal, CLEAR any stale _elevate_*
    // attributes (hygiene; no-ops when nothing is stamped).
    const id = identity();
    const denied = (id.userContext === 'ANONYMOUS');
    const market = cfg().market;
    if (!denied && (!id.customerKey || !id.sessionKey || !market)) return;
    if (cartStamping) return;
    cartStamping = true;
    const root = cartRoot();
    fetch(root + 'cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then(function (r) { if (!r.ok) throw new Error('cart_http_' + r.status); return r.json(); })
      .then(function (cart) {
        const a = (cart && cart.attributes) || {};
        if (denied) {
          if (!a[CART_ATTR_CUSTOMER] && !a[CART_ATTR_SESSION] && !a[CART_ATTR_MARKET]) return null;
          const cleared = {};
          cleared[CART_ATTR_CUSTOMER] = '';
          cleared[CART_ATTR_SESSION] = '';
          cleared[CART_ATTR_MARKET] = '';
          return fetch(root + 'cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes: cleared }),
            keepalive: true
          });
        }
        if (!cart || !cart.item_count) return null;
        if (a[CART_ATTR_CUSTOMER] === id.customerKey && a[CART_ATTR_SESSION] === id.sessionKey && a[CART_ATTR_MARKET] === market) {
          return null;
        }
        const attributes = {};
        attributes[CART_ATTR_CUSTOMER] = id.customerKey;
        attributes[CART_ATTR_SESSION] = id.sessionKey;
        attributes[CART_ATTR_MARKET] = market;
        return fetch(root + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: attributes }),
          keepalive: true
        });
      })
      .catch(function () {})
      .finally(function () { cartStamping = false; });
  }

  window.VoyadoClient = {
    autocomplete: function (params, body) { return runQuery('autocomplete', params, body); },
    searchPage:   function (params, body) { return runQuery('searchPage', params, body); },
    landingPage:  function (params, body) { return runQuery('landingPage', params, body); },
    productPage:  function (params, body) { return runQuery('productPage', params, body); },
    cartPage:     function (params, body) { return runQuery('cartPage', params, body); },
    // Imperative add-to-cart notify for surfaces that can't use the cart:added seam
    // (e.g. the PDP sticky bar). Inherits notify()'s consent + empty-ticket guards.
    notifyAddToCart: function (ticket) { notify('add-to-cart', ticket); },
    // Favourites — by productKey, wired from the wishlist seam (wishlist-client.js).
    notifyAddFavorite: function (productKey) { notifyFavorite('add-favorite', productKey); },
    notifyRemoveFavorite: function (productKey) { notifyFavorite('remove-favorite', productKey); },
    ensureCartStamped: function () { ensureCartStamped(); }
  };

  // Document-level notify wiring. Click fires on mousedown (not click) so it beats
  // navigation and can't race the page unload; ATC from the cart:added event.
  document.addEventListener('mousedown', function (e) {
    const el = e.target.closest('[data-voyado-ticket]');
    if (el) {
      const t = el.getAttribute('data-voyado-ticket');
      if (t) notify('click', t);
    }
  });
  document.addEventListener('cart:added', function (e) {
    const trigger = e.detail && e.detail.trigger;
    const t = trigger && trigger.dataset && trigger.dataset.voyadoTicket;
    if (t) notify('add-to-cart', t);
    ensureCartStamped();
  });

  // On login, set customerKey to the recovered Elevate guest key (window.broraVoyado.elevateKey → customer.id
  // fallback) so pre-login history links to the account via Elevate's same-session Linked keys; logout → fresh
  // anon. See docs/orchestrations/orch_VOYADO_GUEST_KEY_RECOVERY.md.
  function writeSession(s) {
    try { localStorage.setItem(SESSION_KEY, JSON.stringify(s)); } catch (e) { /* quota / private mode */ }
  }
  function syncCustomerIdentity() {
    if (!window.broraVoyado) return;
    if (!trackingAllowed()) {
      // Consent denied/withdrawn — the keys are non-essential storage, so this path leaves nothing
      // of ours on the device. persistentSession() mints a fresh pair once consent is given.
      if (readSession()) clearSession();
      return;
    }
    const cid = window.broraVoyado.customerId;
    const priorKey = window.broraVoyado.elevateKey; // Accelerator-captured guest key (app--… / custom.* fallback)
    const s = persistentSession();
    if (cid != null && cid !== '') {
      const key = String(priorKey || cid); // recovered guest key wins; customer.id is the fallback
      if (s.customerKey !== key) {
        writeSession({ customerKey: key, sessionKey: s.sessionKey, linked: true });
        ensureCartStamped();
      }
    } else if (s.linked) {
      const fresh = { customerKey: randomKey(), sessionKey: randomKey() }; // logged out → fresh anonymous
      writeSession(fresh);
      ensureCartStamped();
    }
  }

  // Re-run identity + cart-stamp on OneTrustGroupsUpdated (fires on grant AND withdrawal) + once on init;
  // trackingAllowed() re-reads OneTrust, and both fns are idempotent.
  function onConsentChange() {
    syncCustomerIdentity();
    ensureCartStamped();
  }
  window.addEventListener('OneTrustGroupsUpdated', onConsentChange);
  onConsentChange(); // initial run
})();
